import { toast } from "sonner";

import processorUrl from "./pcm-worklet-processor.js?url";

const PCM16_MIN_VALUE = 0x80_00;
const PCM16_MAX_VALUE = 0x7f_ff;

export type AudioConfig = {
  inputDevice: {
    deviceId: string;
    label: string;
    kind: "audioinput" | "audiooutput";
    groupId?: string;
  } | null;
  sampleRate: number;
  channels: number;
  // When present, mic audio goes to channel 0 (agent) and this stream to channel 1 (customer).
  systemAudioStream?: MediaStream | null;
};

export type AudioRecorder = {
  startRecording: (
    config: AudioConfig,
    onAudioData: (audioData: { audioBuffer: number[] }) => void,
  ) => Promise<void>;
  stopRecording: () => void;
  isRecording: boolean;
};

class WebAudioRecorder implements AudioRecorder {
  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private systemAudioSourceNode: MediaStreamAudioSourceNode | null = null;
  private analyserNode: AnalyserNode | null = null;
  private isRecordingFlag = false;
  private isStartingFlag = false;
  private onAudioDataCallback: ((audioData: { audioBuffer: number[] }) => void) | null = null;

  get isRecording(): boolean {
    return this.isRecordingFlag;
  }

  get analyser(): AnalyserNode | null {
    return this.analyserNode;
  }

  async startRecording(
    config: AudioConfig,
    onAudioData: (audioData: { audioBuffer: number[] }) => void,
  ): Promise<void> {
    if (this.isRecordingFlag || this.isStartingFlag) {
      return;
    }

    this.isStartingFlag = true;
    this.onAudioDataCallback = onAudioData;

    // Stereo only when the caller has already captured a system audio stream.
    // Without it we fall back to mono regardless of the channels setting.
    const stereo = config.channels === 2 && !!config.systemAudioStream;

    try {
      const constraints = this.createAudioConstraints(config);
      this.mediaStream = await this.getUserMediaWithFallback(constraints);
      this.audioContext = new AudioContext({ sampleRate: config.sampleRate });

      await this.audioContext.audioWorklet.addModule(processorUrl);

      this.analyserNode = this.audioContext.createAnalyser();
      this.analyserNode.fftSize = 256;
      this.workletNode = new AudioWorkletNode(this.audioContext, "pcm-processor", {
        processorOptions: { stereo },
        channelCount: stereo ? 2 : 1,
        channelCountMode: "explicit",
      });

      this.workletNode.port.onmessage = (event: MessageEvent<Float32Array>) => {
        this.processAudioData(event.data);
      };

      let stereoConnected = false;
      if (stereo && config.systemAudioStream) {
        try {
          // Merge mic (left/agent) and system audio (right/customer) into a single
          // stereo stream. ChannelMergerNode takes N mono inputs → one N-channel output.
          const merger = this.audioContext.createChannelMerger(2);
          this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream);
          this.systemAudioSourceNode = this.audioContext.createMediaStreamSource(
            config.systemAudioStream,
          );
          this.sourceNode.connect(merger, 0, 0); // mic  → left
          this.systemAudioSourceNode.connect(merger, 0, 1); // sys  → right
          merger.connect(this.analyserNode);
          stereoConnected = true;
        } catch {
          toast.warning("Customer audio capture failed — recording agent only.");
          if (this.systemAudioSourceNode) {
            this.systemAudioSourceNode.disconnect();
            this.systemAudioSourceNode = null;
          }
          if (this.sourceNode) {
            this.sourceNode.disconnect();
            this.sourceNode = null;
          }
        }
      }
      if (!stereoConnected) {
        this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream);
        this.sourceNode.connect(this.analyserNode);
      }

      this.analyserNode.connect(this.workletNode);
      this.workletNode.connect(this.audioContext.destination);

      this.isRecordingFlag = true;
    } catch (error) {
      this.cleanup();
      throw error;
    } finally {
      this.isStartingFlag = false;
    }
  }

  stopRecording(): void {
    if (!this.isRecordingFlag) {
      return;
    }
    this.cleanup();
  }

  // eslint-disable-next-line no-undef
  private createAudioConstraints(config: AudioConfig): MediaStreamConstraints {
    // eslint-disable-next-line no-undef
    const audioConstraints: MediaTrackConstraints = {
      sampleRate: { ideal: config.sampleRate },
      // Mic is always captured as mono. Customer audio arrives on a separate
      // stream (systemAudioStream) and is merged in Web Audio, not here.
      channelCount: { ideal: 1 },
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
    };

    if (config.inputDevice?.deviceId) {
      audioConstraints.deviceId = { ideal: config.inputDevice.deviceId };
    }

    return { audio: audioConstraints };
  }

  private async getUserMediaWithFallback(
    // eslint-disable-next-line no-undef
    constraints: MediaStreamConstraints,
  ): Promise<MediaStream> {
    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (error) {
      if (error instanceof Error && error.name === "OverconstrainedError") {
        // eslint-disable-next-line no-undef
        const fallbackConstraints: MediaStreamConstraints = {
          audio: {
            deviceId:
              constraints.audio &&
              typeof constraints.audio === "object" &&
              "deviceId" in constraints.audio
                ? constraints.audio.deviceId
                : undefined,
          },
        };
        return await navigator.mediaDevices.getUserMedia(fallbackConstraints);
      }

      throw error;
    }
  }

  private processAudioData(channelData: Float32Array): void {
    if (!this.onAudioDataCallback) {
      return;
    }

    const pcm16 = new Int16Array(channelData.length);

    for (let i = 0; i < channelData.length; i++) {
      const sample = Math.max(-1, Math.min(1, channelData[i]));
      pcm16[i] = sample < 0 ? sample * PCM16_MIN_VALUE : sample * PCM16_MAX_VALUE;
    }

    this.onAudioDataCallback({
      audioBuffer: Array.from(new Uint8Array(pcm16.buffer)),
    });
  }

  private cleanup(): void {
    this.isRecordingFlag = false;
    this.isStartingFlag = false;

    if (this.workletNode) {
      this.workletNode.port.onmessage = null;
      this.workletNode.disconnect();
      this.workletNode = null;
    }

    if (this.analyserNode) {
      this.analyserNode.disconnect();
      this.analyserNode = null;
    }

    // Disconnect system audio source but do NOT stop its tracks — the stream
    // is owned by the SystemAudioContext and persists across calls.
    if (this.systemAudioSourceNode) {
      this.systemAudioSourceNode.disconnect();
      this.systemAudioSourceNode = null;
    }

    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }

    if (this.mediaStream) {
      for (const track of this.mediaStream.getTracks()) {
        track.stop();
      }
      this.mediaStream = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    this.onAudioDataCallback = null;
  }
}

export const audioRecorder = new WebAudioRecorder();
